import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ApiError } from "@/api/client";
import {
  useConnections,
  useCreateConnection,
  useSeedDemoConnections,
  useSourceTypes,
  useTestDraft,
  useUploadSource,
  type ConnectionSummary,
  useIsAdmin,
} from "@/api/queries";
import { FileDrop } from "@/components/sources/FileDrop";
import { Field } from "@/components/ui/Field";
import { formatBytes } from "@/lib/bytes";
import { DiagnosticsReport } from "@/components/sources/DiagnosticsReport";
import { SourceForm } from "@/components/sources/SourceForm";
import { emptyValue, toPayload, type SourceFormValue } from "@/components/sources/formState";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip, StatusDot } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  ChevronRightIcon,
  DatabaseIcon,
  PlugIcon,
  PlusIcon,
  SparkIcon,
  UploadIcon,
} from "@/components/icons";

function describeTarget(connection: ConnectionSummary): string {
  if (connection.file) {
    return `${connection.file.original_name} · ${connection.file.kind_label} · ${formatBytes(connection.file.bytes)}`;
  }
  if (connection.file_path) return connection.file_path;
  const host = connection.host ?? "";
  const port = connection.port ? `:${connection.port}` : "";
  return `${host}${port}/${connection.database ?? ""}`;
}

function SourceRow({ connection }: { connection: ConnectionSummary }) {
  const status = connection.schema_status;
  const tone =
    connection.last_test_ok === false ? "error" : connection.last_test_ok ? "success" : "neutral";
  return (
    <li>
      <Link
        to="/sources/$connectionId"
        params={{ connectionId: connection.id }}
        className="flex items-center gap-4 rounded-container border border-border bg-card px-4 py-4 hover:border-border-emphasized hover:bg-hover motion-fast"
      >
        <span className="text-ink-disabled">
          <DatabaseIcon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="type-h4 text-ink">{connection.name}</span>
            <Chip tone="neutral">
              {connection.file ? connection.file.kind_label : connection.source_type}
            </Chip>
            {connection.file ? (
              <Chip tone="accent">uploaded</Chip>
            ) : connection.managed ? (
              <Chip tone="accent">managed</Chip>
            ) : null}
            {connection.has_password ? <Chip tone="neutral">credentialled</Chip> : null}
            {!connection.has_native_comments && !connection.has_data_dictionary ? (
              <Chip tone="warning">undocumented</Chip>
            ) : null}
            {/* The verdict, not the fact that a check ran: "which sources are
                ready to hand to analysts?" is the question this list answers
                (PRD FR-1.8). */}
            {connection.health_verdict ? (
              <Chip
                tone={
                  connection.health_verdict === "ready"
                    ? "success"
                    : connection.health_verdict === "review"
                      ? "warning"
                      : "error"
                }
              >
                {connection.health_verdict === "ready"
                  ? "health: ready"
                  : connection.health_verdict === "review"
                    ? "health: review"
                    : "health: needs attention"}
              </Chip>
            ) : null}
          </span>
          <span className="type-support text-ink-secondary mt-1 block truncate">
            {connection.description || describeTarget(connection)}
          </span>
          <span className="type-caption text-ink-disabled mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="flex items-center gap-1.5">
              <StatusDot tone={tone} />
              {connection.last_test_ok === null
                ? "not tested"
                : connection.last_test_ok
                  ? "connection verified"
                  : "connection failed"}
            </span>
            <span className="tabular-nums">
              {status.relation_count} relations
              {status.commented_count > 0 ? ` · ${status.commented_count} commented` : ""}
            </span>
            {connection.has_data_dictionary ? <span>dictionary written</span> : null}
          </span>
        </span>
        <span className="text-ink-disabled">
          <ChevronRightIcon className="size-4" />
        </span>
      </Link>
    </li>
  );
}

/** The name the server gives a source when none is typed (`_default_name`). */
function defaultSourceName(filename: string): string {
  const stem = filename.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ").trim();
  return stem ? stem.charAt(0).toUpperCase() + stem.slice(1) : "Uploaded file";
}

/**
 * Upload a file and get a source (FILE-2). The file decides what kind of
 * source it is, so the only question asked is an optional name.
 */
function UploadSourceCard({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const upload = useUploadSource();
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) return;
    setError(null);
    setProgress(0);
    upload.mutate(
      { file, name, onProgress: setProgress },
      {
        onSuccess: (created) => {
          void navigate({ to: "/sources/$connectionId", params: { connectionId: created.id } });
        },
        onError: (caught) => {
          setProgress(null);
          setError(caught instanceof ApiError ? caught.message : "The file could not be uploaded.");
        },
      },
    );
  };

  return (
    <Card className="mb-8">
      <CardHeader
        title="Upload a file"
        description="A database is queried as it is; anything else becomes a table per file, or per sheet. Every query is read-only."
      />
      <CardBody>
        <form onSubmit={submit} className="flex flex-col gap-5">
          <FileDrop
            file={file}
            disabled={upload.isPending}
            progress={progress}
            onFile={(chosen) => {
              setFile(chosen);
              setError(null);
            }}
          />
          <Field
            label="Source name"
            value={name}
            maxLength={120}
            placeholder={file ? defaultSourceName(file.name) : "Named after the file"}
            hint="Optional. You can rename it later."
            onChange={(event) => setName(event.target.value)}
          />
          {error ? (
            <p role="alert" className="type-support text-error rounded-element bg-error-subtle p-3">
              {error}
            </p>
          ) : null}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="submit"
              variant="primary"
              icon={<UploadIcon className="size-4" />}
              loading={upload.isPending}
              disabled={!file}
            >
              Upload and create source
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={upload.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

export function SourcesPage() {
  const navigate = useNavigate();
  const { data: connections, isLoading } = useConnections();
  const isAdmin = useIsAdmin();
  const { data: sourceTypes } = useSourceTypes();
  const create = useCreateConnection();
  const testDraft = useTestDraft();
  const seedDemo = useSeedDemoConnections();

  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [value, setValue] = useState<SourceFormValue>(() => emptyValue("postgres"));
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setAdding(false);
    setValue(emptyValue("postgres"));
    setError(null);
    testDraft.reset();
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    create.mutate(toPayload(value, false), {
      onSuccess: (created) => {
        reset();
        void navigate({ to: "/sources/$connectionId", params: { connectionId: created.id } });
      },
      onError: (caught) =>
        setError(caught instanceof ApiError ? caught.message : "The source could not be saved."),
    });
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="type-caption text-ink-disabled">Deployment</p>
          <h1 className="type-h1 text-ink mt-2">Data sources</h1>
          <p className="type-body text-ink-secondary mt-2 max-w-2xl">
            Every source is read-only by construction: guarded SQL, an engine-level read-only
            session, a statement timeout and a row cap.
          </p>
        </div>
        {!adding && !uploading && isAdmin ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button icon={<PlusIcon className="size-4" />} onClick={() => setAdding(true)}>
              Connect a database
            </Button>
            <Button
              variant="primary"
              icon={<UploadIcon className="size-4" />}
              onClick={() => setUploading(true)}
            >
              Upload a file
            </Button>
          </div>
        ) : null}
      </header>

      {uploading ? <UploadSourceCard onClose={() => setUploading(false)} /> : null}

      {adding && sourceTypes ? (
        <Card className="mb-8">
          <CardHeader
            title="Add a source"
            description="Test the parameters before saving; nothing is stored until you do."
          />
          <CardBody>
            <SourceForm
              // A file source is made by uploading the file, never by a form.
              sourceTypes={sourceTypes.filter((entry) => entry.key !== "file")}
              value={value}
              onChange={setValue}
              onSubmit={submit}
              error={error}
            >
              <Button type="submit" variant="primary" loading={create.isPending}>
                Save source
              </Button>
              <Button
                type="button"
                icon={<PlugIcon className="size-4" />}
                loading={testDraft.isPending}
                onClick={() => {
                  setError(null);
                  testDraft.mutate(toPayload(value, false), {
                    onError: (caught) =>
                      setError(
                        caught instanceof ApiError ? caught.message : "The test could not run.",
                      ),
                  });
                }}
              >
                Test connection
              </Button>
              <Button type="button" variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </SourceForm>
            {testDraft.data ? (
              <div className="mt-5">
                <DiagnosticsReport report={testDraft.data} />
              </div>
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {isLoading ? (
        <p className="type-support text-ink-secondary">Loading sources…</p>
      ) : connections && connections.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {connections.map((connection) => (
            <SourceRow key={connection.id} connection={connection} />
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={<DatabaseIcon className="size-8" />}
          title="No sources yet"
          detail={
            isAdmin
              ? "Connect a database, upload a file, or start with the bundled surf-shop demo data."
              : "Connecting a source needs the administrator key."
          }
          action={
            isAdmin ? (
              <Button
                variant="subtle"
                icon={<SparkIcon className="size-4" />}
                loading={seedDemo.isPending}
                onClick={() => seedDemo.mutate()}
              >
                Add the demo sources
              </Button>
            ) : undefined
          }
        />
      )}
    </div>
  );
}
